import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from '@page/icons';

import { IconButton } from '../IconButton';
import { cn } from '../../lib/styles';
import { ErrorBoundary } from '../../components/ErrorBoundary';

interface DialogTitleProps {
  children: React.ReactNode;
  description?: React.ReactNode;
  closeButton?: React.ReactNode;
  className?: string;
  onClose?: () => void;
  suffix?: React.ReactNode;
  headingPrefix?: React.ReactNode;
}

type DialogActionsProps = React.HTMLAttributes<HTMLDivElement>;

export interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export function DialogClose({ asChild = true, ...rest }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" asChild={asChild} {...rest} />;
}

export const DialogTitle = ({
  children,
  closeButton: _closeButton,
  description: _description,
  onClose,
  className,
  headingPrefix,
  suffix,
}: DialogTitleProps) => {
  const h2Element = <h2 className="font-heading-xl font-semibold text-text-primary">{children}</h2>;

  const renderCloseButton = () => {
    if (typeof _closeButton !== 'undefined') return _closeButton;

    const button = (
      <IconButton onClick={onClose}>
        <XIcon className="size-5" fill="currentColor" />
      </IconButton>
    );

    if (onClose) return button;

    return <DialogClose asChild>{button}</DialogClose>;
  };

  const closeButton = renderCloseButton();

  const header = (
    <div className="flex items-center justify-between gap-4 h-6">
      <div className="flex items-center gap-2">
        {headingPrefix}
        {h2Element}
      </div>

      {suffix}

      {closeButton}
    </div>
  );

  const descriptionElement = _description ? (
    <p className="font-body-sm font-medium text-text-tertiary pr-10">{_description}</p>
  ) : null;

  return (
    <ErrorBoundary
      fallback={
        <div className={cn('flex flex-col gap-2 py-5 pl-5 pr-2.5 border-b border-border-default', className)}>
          {header}

          {descriptionElement}
        </div>
      }
    >
      <DialogPrimitive.Title
        className={cn('flex flex-col gap-2 py-5 pl-5 pr-2.5 border-b border-border-default', className)}
      >
        {header}

        {descriptionElement}
      </DialogPrimitive.Title>
    </ErrorBoundary>
  );
};

export const DialogFooter = ({ children, className, ...rest }: DialogActionsProps) => {
  return (
    <div
      className={cn('flex items-center justify-end gap-3 py-4 px-5 border-t border-border-default', className)}
      {...rest}
    >
      {children}
    </div>
  );
};

export const DialogContent = ({ children, className, ...rest }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={cn('px-5 py-6', className)} {...rest}>
      {children}
    </div>
  );
};

export const DialogLayout = ({ children, className }: DialogContentProps) => {
  return (
    <div className={cn('flex flex-col bg-white shadow-lg border border-border-default rounded-[20px]', className)}>
      {children}
    </div>
  );
};
